import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
export type LegalModalKey = 'privacy' | 'terms' | 'refund' | 'contact' | null;
interface LegalModalProps {
  activeKey: LegalModalKey;
  onClose: () => void;
}
const CONTENT: Record<Exclude<LegalModalKey, null>, {
  title: string;
  body: React.ReactNode;
}> = {
  privacy: {
    title: 'Política de Privacidad',
    body: <>
        <p data-id="element-172">
          En EpikkaStore valoramos profundamente tu privacidad y nos
          comprometemos a proteger tus datos personales.
        </p>
        <h3 data-id="element-173">Información que recopilamos</h3>
        <p data-id="element-174">
          Cuando realizas una compra, recopilamos únicamente los datos
          necesarios para procesar tu pedido: nombre, correo electrónico y datos
          de pago. No almacenamos información de tarjetas de crédito en nuestros
          servidores; el procesamiento se realiza a través de pasarelas de pago
          seguras y certificadas.
        </p>
        <h3 data-id="element-175">Uso de tu información</h3>
        <p data-id="element-176">
          Usamos tu correo electrónico exclusivamente para enviarte el acceso a
          tu producto digital, confirmaciones de compra y, ocasionalmente,
          contenido relacionado con tu compra. Nunca compartimos ni vendemos tu
          información a terceros.
        </p>
        <h3 data-id="element-177">Cookies</h3>
        <p data-id="element-178">
          Nuestro sitio utiliza cookies para mejorar tu experiencia de
          navegación. Puedes desactivarlas desde la configuración de tu
          navegador si lo prefieres.
        </p>
        <h3 data-id="element-179">Tus derechos</h3>
        <p data-id="element-180">
          Tienes derecho a solicitar acceso, corrección o eliminación de tus
          datos personales en cualquier momento. Para ejercer estos derechos,
          escríbenos a{' '}
          <a href="mailto:epikkastore@gmail.com" className="text-clay underline" data-id="element-181">
            epikkastore@gmail.com
          </a>
          .
        </p>
        <p className="text-xs text-muted italic pt-2" data-id="element-182">
          Última actualización: Mayo 2026
        </p>
      </>
  },
  terms: {
    title: 'Términos y Condiciones',
    body: <>
        <p data-id="element-183">
          Al adquirir cualquier producto digital de EpikkaStore, aceptas los
          siguientes términos:
        </p>
        <h3 data-id="element-184">Naturaleza del producto</h3>
        <p data-id="element-185">
          "Apaga Tu Inflamación" es un producto digital descargable en formato
          PDF. No se realiza envío físico de ningún tipo. El acceso se entrega
          de forma inmediata tras confirmar la compra.
        </p>
        <h3 data-id="element-186">Uso personal</h3>
        <p data-id="element-187">
          El contenido adquirido es para uso estrictamente individual y
          personal. No está permitida la reventa, redistribución, copia parcial
          o total del material, ni compartir el archivo con terceros.
        </p>
        <h3 data-id="element-188">Propiedad intelectual</h3>
        <p data-id="element-189">
          Todos los textos, recetas, plantillas, metodologías y diseños
          incluidos en esta guía están protegidos por derechos de autor a nombre
          de EpikkaStore.
        </p>
        <h3 data-id="element-190">Naturaleza educativa</h3>
        <p data-id="element-191">
          El contenido tiene fines exclusivamente educativos e informativos. No
          sustituye en ningún caso el diagnóstico, tratamiento ni consejo médico
          profesional.
        </p>
        <h3 data-id="element-192">Modificaciones</h3>
        <p data-id="element-193">
          EpikkaStore se reserva el derecho de actualizar estos términos en
          cualquier momento. Los cambios se publicarán en esta misma página.
        </p>
        <h3 data-id="element-194">Aceptación</h3>
        <p data-id="element-195">
          Al completar tu compra, confirmas haber leído y aceptado estos
          términos y condiciones.
        </p>
        <p className="text-xs text-muted italic pt-2" data-id="element-196">
          Última actualización: Mayo 2026
        </p>
      </>
  },
  refund: {
    title: 'Política de Reembolso',
    body: <>
        <p data-id="element-197">Queremos que esta decisión te sume calma, no presión.</p>
        <h3 data-id="element-198">Garantía de 7 días</h3>
        <p data-id="element-199">
          Ofrecemos una garantía de satisfacción de 7 días naturales contados
          desde la fecha de tu compra. Si dentro de ese período sientes que esta
          guía no es para ti, puedes solicitar la devolución del 100% de tu
          inversión.
        </p>
        <h3 data-id="element-200">Cómo solicitar tu reembolso</h3>
        <p data-id="element-201">
          Envía un correo a{' '}
          <a href="mailto:epikkastore@gmail.com" className="text-clay underline" data-id="element-202">
            epikkastore@gmail.com
          </a>{' '}
          con el asunto "Solicitud de reembolso" e incluye:
        </p>
        <ul data-id="element-203">
          <li data-id="element-204">Tu nombre completo</li>
          <li data-id="element-205">Correo electrónico usado en la compra</li>
          <li data-id="element-206">Fecha aproximada de compra</li>
        </ul>
        <p data-id="element-207">
          No necesitas justificar tu decisión. Procesamos las devoluciones en un
          plazo máximo de 5 días hábiles a través del mismo medio de pago
          utilizado.
        </p>
        <h3 data-id="element-208">Excepciones</h3>
        <p data-id="element-209">
          Por la naturaleza digital del producto, las solicitudes realizadas
          después del séptimo día no podrán ser procesadas.
        </p>
        <h3 data-id="element-210">Compromiso</h3>
        <p data-id="element-211">
          Nuestro objetivo es que esta guía realmente te sume. Si no es así,
          preferimos devolverte tu inversión antes que tener un cliente
          insatisfecho.
        </p>
        <p className="text-xs text-muted italic pt-2" data-id="element-212">
          Última actualización: Mayo 2026
        </p>
      </>
  },
  contact: {
    title: 'Contacto y Soporte',
    body: <>
        <p data-id="element-213">Estamos aquí para acompañarte.</p>
        <h3 data-id="element-214">Soporte al cliente</h3>
        <p data-id="element-215">
          Si tienes dudas sobre tu compra, problemas para acceder a tu guía, o
          cualquier consulta sobre el contenido, escríbenos. Te respondemos con
          cariño y rapidez.
        </p>
        <h3 data-id="element-216">Correo de soporte</h3>
        <p data-id="element-217">
          <a href="mailto:epikkastore@gmail.com" className="text-clay underline font-medium" data-id="element-218">
            epikkastore@gmail.com
          </a>
        </p>
        <h3 data-id="element-219">Facebook</h3>
        <p data-id="element-220">
          Síguenos como{' '}
          <a href="https://www.facebook.com/profile.php?id=61572064552166" target="_blank" rel="noopener noreferrer" className="text-clay underline" data-id="element-221">
            Epikka Store
          </a>{' '}
          para contenido sobre bienestar femenino, recetas antiinflamatorias y
          novedades.
        </p>
        <h3 data-id="element-222">Tiempo de respuesta</h3>
        <p data-id="element-223">
          Respondemos todos los correos dentro de las 24–48 horas hábiles (lunes
          a viernes).
        </p>
        <h3 data-id="element-224">Antes de escribirnos</h3>
        <p data-id="element-225">
          Si tu duda es sobre el contenido, política de devoluciones o cómo
          acceder a tu guía, revisa primero la sección de Preguntas Frecuentes
          en esta misma página. Es probable que encuentres tu respuesta de forma
          inmediata.
        </p>
        <h3 data-id="element-226">Sobre EpikkaStore</h3>
        <p data-id="element-227">
          Somos una marca dedicada al bienestar femenino integral. Creamos guías
          digitales pensadas con cariño y rigor, para mujeres que quieren
          entender y cuidar su cuerpo desde un enfoque realista y sostenible.
        </p>
        <p className="italic text-ink/80 pt-2" data-id="element-228">
          Gracias por confiar en nosotras.
        </p>
      </>
  }
};
export function LegalModal({
  activeKey,
  onClose
}: LegalModalProps) {
  // Close on ESC + notify rest of the app so things like social-proof toasts can pause
  useEffect(() => {
    if (!activeKey) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    // Lock body scroll while modal is open
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Broadcast open
    window.dispatchEvent(new CustomEvent('legal-modal:open'));
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      // Broadcast close
      window.dispatchEvent(new CustomEvent('legal-modal:close'));
    };
  }, [activeKey, onClose]);
  const content = activeKey ? CONTENT[activeKey] : null;
  return <AnimatePresence data-id="element-229">
      {activeKey && content && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 bg-ink/50 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-230">
          <motion.div initial={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="relative bg-cream rounded-xl shadow-2xl w-full max-w-[600px] max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-231">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-6 md:px-8 pt-6 pb-4 border-b border-taupe/15 shrink-0" data-id="element-232">
              <h2 id="legal-modal-title" className="font-serif text-2xl md:text-3xl text-ink leading-tight" data-id="element-233">
                {content.title}
              </h2>
              <button onClick={onClose} aria-label="Cerrar" className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-clay hover:bg-clay/10 transition-colors" data-id="element-234">
                <X className="w-5 h-5" data-id="element-235" />
              </button>
            </div>

            {/* Body (scrollable) */}
            <div className="overflow-y-auto px-6 md:px-8 py-6 text-ink/85 text-[15px] leading-relaxed legal-modal-body" data-id="element-236">
              {content.body}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}